import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import { ReactComponent as AppIcon } from 'assets/svg/header-app.svg'
import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import useLocale from 'hooks/useLocale'
import useTranslation from 'hooks/useTranslation'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import getHeaderDropdownAppData from 'utils/getHeaderDropdownAppData'
import BadgeDropdown from '../BadgeDropdown/BadgeDropdown'
import PreviewBlock from './PreviewBlock'
import { isEmptyHeaderPage } from '../../../../constants'

const DropdownApp: FC<IOwnProps> = ({ type }) => {
  const [isActive, setIsActive] = useState(false)
  const { locale, pathname } = useLocale()
  const { t } = useTranslation()

  const isBlog = isEmptyHeaderPage(pathname)
  const { data, previewBlockData } = getHeaderDropdownAppData(t, locale, isBlog)

  return (
    <DropdownContainer
      onClick={() => setIsActive(true)}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}>
      <NameContainer $type={type}>
        <AppIconStyled $type={type} />
      </NameContainer>
      <DropdownWrapper $isActive={isActive}>
        <PreviewBlock
          title={previewBlockData.title}
          preview={previewBlockData.image}
          isButton={false}
          buttonLabel={previewBlockData.buttonLabel}
          text={previewBlockData.previewDescription}
          href={''}
        />
        <DropdownItemGridContainer>
          <DropdownTitle>{data.chapter1.title}</DropdownTitle>

          <DropdownItemWrapper>
            {data.chapter1.data.map((item: any, index: number) => {
              return (
                <DropdownItem
                  onClick={() => item?.showChat(true)}
                  key={index}
                  to={item.href}
                  withLocale={true}
                  includeLangsArr={item?.includeLangsArr}
                  replace>
                  <NameWrapper>
                    <DropdownItemName>{item.title}</DropdownItemName>
                    {item.badgeType && <BadgeDropdown type={item.badgeType} />}
                  </NameWrapper>
                  <DropdownItemText>{item.text}</DropdownItemText>
                </DropdownItem>
              )
            })}
          </DropdownItemWrapper>
          <DropdownTitle>{data.chapter2.title}</DropdownTitle>
          <DropdownItemWrapper>
            {data.chapter2.data.map((item: any, index: number) => (
              <DropdownItem
                key={index}
                to={item.href}
                withLocale={true}
                includeLangsArr={item?.includeLangsArr}
                replace>
                <NameWrapper>
                  <DropdownItemName>{item.title}</DropdownItemName>
                  {item.badgeType && <BadgeDropdown type={item.badgeType} />}
                </NameWrapper>
                <DropdownItemText>{item.text}</DropdownItemText>
              </DropdownItem>
            ))}
          </DropdownItemWrapper>
        </DropdownItemGridContainer>
      </DropdownWrapper>
    </DropdownContainer>
  )
}

export default DropdownApp

const DropdownContainer = styled.div`
  position: relative;
  -webkit-tap-highlight-color: transparent;
`
const DropdownWrapper = styled.div<{ $isActive?: boolean }>`
  display: ${({ $isActive }) => ($isActive ? 'grid' : 'none')};
  grid-template-columns: 1fr 1fr;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  min-width: 10rem;
  margin: 2rem 0 0;
  font-size: ${({ theme: { fontSize } }) => fontSize.base};
  color: ${({ theme: { colors } }) => colors.default};
  text-align: left;
  list-style: none;
  background-clip: padding-box;
  width: fit-content;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  box-shadow: 0px 15px 50px rgba(15, 42, 102, 0.7);
  border-radius: 0.5rem;

  transition: opacity 0.4s, visibility 0.4s;

  &::before {
    content: '';
    height: 35px;
    top: -35px;
    left: 0;
    right: 0;
    z-index: -1;
    position: absolute;
  }

  @media ${({ theme }) => theme.device.lgMax} {
    left: 5%;

    &::after {
      left: 55%;
    }
  }
`

const NameContainer = styled.div<{ $type: THeaderType }>`
  display: flex;
  align-items: center;
  color: ${({ theme: { colors }, $type }) => ($type === 'secondary' ? colors.white : colors.newLightColor.paleWhite)};
  font-weight: 600;
  font-size: 14px;

  &::after {
    display: inline-block;
    margin-inline-start: 6px;
    vertical-align: 3.96px;
    content: '';
    width: 9px;
    height: 6px;
    margin-top: 4px;
    border-style: solid;
    border-width: 6px 5px 0 5px;
    border-color: #6b7b9f transparent transparent transparent;
  }

  &:hover {
    ::after {
      transition: all 0.1s ease-out;
      border-color: ${({ theme: { colors }, $type }) =>
          $type === 'primary' ? '#97b8f7' : colors.newDarkColor.cyanBlue}
        transparent transparent transparent;
      transform: rotate(180deg);
    }
  }
`

const AppIconStyled = styled(AppIcon)<{ $type: THeaderType }>`
  > rect {
    fill: ${({ $type, theme }) => $type === 'primary' && theme.colors.newDarkColor.vividBlue};
    stroke: ${({ $type, theme }) => $type === 'primary' && theme.colors.newDarkColor.vividBlue};
  }
`

const DropdownItemGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  row-gap: 0.875rem;
  column-gap: 2.75rem;
  padding: 2rem 2rem;
  padding-top: 0;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  border-radius: 0.5rem;
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  width: 300px;
  padding: 0.625rem 0.75rem;
  text-align: start;

  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  color: ${({ theme: { colors } }) => colors.dropdownItem};
  text-align: inherit;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    > h6 {
      color: ${({ theme: { colors } }) => colors.link};
    }

    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        left: 0;
        right: unset;
        transform: rotate(180deg);
      }
    }
  }
`
const DropdownTitle = styled.h6`
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 1rem;
  margin-top: 2rem;
  margin-bottom: 0.875rem;
  margin-inline-start: 0.75rem;
  text-align: start;
`
const DropdownItemWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  column-gap: 2.75rem;
  row-gap: 0.25rem;
  text-align: start;
`

const NameWrapper = styled.div`
  align-items: center;
  text-align: start;
`
const DropdownItemName = styled.span`
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 0.875rem;
  margin-inline-end: 0.5rem;
`
const DropdownItemText = styled.p`
  width: 240px;
  margin: 0;
  margin-top: 2px;
  margin-inline-end: auto;
  white-space: pre-wrap;
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  font-size: 0.875rem;
  opacity: 0.8;
`

interface IOwnProps {
  type: THeaderType
}
